/** @format — Received / Expected / Overdue status tabs */

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import type { ShipmentDocumentTab } from "../../shipments/hooks/useShipmentDocuments";

const TABS: Array<{ id: ShipmentDocumentTab; label: string }> = [
  { id: "received", label: "Received" },
  { id: "expected", label: "Expected" },
  { id: "overdue", label: "Overdue" },
];

function useIsMobileViewport() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return isMobile;
}

export default function DocumentsStatusTabs({
  activeTab,
  onChange,
  counts,
  variant = "page",
}: {
  activeTab: ShipmentDocumentTab;
  onChange: (tab: ShipmentDocumentTab) => void;
  counts: { received: number; expected: number; overdue: number };
  /** drawer = full-width segmented tabs in shipment drawer; page = compact on desktop */
  variant?: "page" | "drawer";
}) {
  const isMobileViewport = useIsMobileViewport();
  const isSegmented = variant === "drawer" || (variant === "page" && isMobileViewport);

  return (
    <div
      className={cn(
        "flex shrink-0 rounded-lg bg-neutral-100 p-0.5",
        isSegmented ? "h-8 w-full" : "inline-flex max-w-full",
      )}
    >
      {TABS.map((tab) => {
        const isActive = activeTab === tab.id;
        const count = counts[tab.id];
        const overdueBadge = tab.id === "overdue" && count > 0;

        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={cn(
              "flex items-center gap-1 rounded-md text-xs font-medium leading-none text-[#0a0a0a] transition-colors",
              isSegmented
                ? "h-full min-h-0 flex-1 justify-center px-1.5"
                : "shrink-0 whitespace-nowrap px-2 py-0.5 sm:px-1.5 sm:py-px",
              isActive && "bg-white shadow-[0px_1px_3px_0_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)]",
            )}
          >
            {tab.label}
            {overdueBadge ? (
              <span
                className={cn(
                  "flex h-4 min-w-4 items-center justify-center rounded-[10px] px-1 text-xs font-semibold leading-none",
                  isActive ? "bg-red-500 text-white" : "bg-neutral-300 text-[#0a0a0a]",
                )}
              >
                {count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
